import { Component, inject, OnInit } from '@angular/core'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { MatDivider } from '@angular/material/divider'
import { MinutesStylingComponent } from '../minutes-styling/minutes-styling.component'
import { FormsModule } from '@angular/forms'
import { AsyncPipe } from '@angular/common'
import { SettingsService } from '../settings.service'
import { distinctUntilChanged, filter, map, Observable } from 'rxjs'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatOption } from '@angular/material/autocomplete'
import { MatSelect } from '@angular/material/select'
import { CalendarProviderName, User, UsersService, UserWithOrganization } from '../users.service'
import { Organization } from '../organizations.service'
import { MatButton } from '@angular/material/button'
import { ToastService } from '../toast.service'
import { CallsService, MinutesPassive, MinutesStyling, MetricType } from '../calls.service'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ActivatedRoute } from '@angular/router'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatChip } from '@angular/material/chips'
import { MatTooltip } from '@angular/material/tooltip'
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component'
import { MatDialog } from '@angular/material/dialog'
import { SpinnerComponent } from '../spinner/spinner.component'
import { LoggerService } from '../logger.service'
import { JoinSettingsComponent } from '../join-settings/join-settings.component'
import { MatCheckbox } from '@angular/material/checkbox'
import { MetricsService } from '../metrics.service'
import { AnalyticsDashboardComponent } from '../analytics-dashboard/analytics-dashboard.component'

export type JoinSettings = {
  joinMeetingDeclined: boolean
  joinMeetingTentativelyAccepted: boolean
  joinMeetingUnresponded: boolean
  joinMeetingExternal: boolean
  joinMeetingInternal: boolean
}

const SectionTranslationKeyMap: { [key: string]: string } = {
  general: 'SETTINGS.GENERAL_SETTINGS',
  language: 'SETTINGS.LANGUAGE_SETTINGS',
  calendar: 'SETTINGS.CALENDAR_SETTINGS',
  assistant: 'SETTINGS.ASSISTANT_SETTINGS',
  'minutes-styling': 'SETTINGS.MINUTING_SETTINGS',
  notifications: 'SETTINGS.NOTIFICATION_SETTINGS',
  transcript: 'SETTINGS.TRANSCRIPT_SETTINGS',
  sharing: 'SETTINGS.SHARING_SETTINGS',
  analytics: 'SETTINGS.USAGE_ANALYTICS_SETTINGS',
}

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    TranslateModule,
    MatDivider,
    MinutesStylingComponent,
    FormsModule,
    AsyncPipe,
    SpinnerComponent,
    MatFormField,
    MatLabel,
    MatOption,
    MatSelect,
    MatButton,
    ToolbarComponent,
    MatSlideToggle,
    MatChip,
    MatTooltip,
    JoinSettingsComponent,
    MatCheckbox,
    AnalyticsDashboardComponent,
  ],
  templateUrl: './settings-page.component.html',
  styleUrl: './settings-page.component.scss',
})
export class SettingsPageComponent implements OnInit {
  settingsService = inject(SettingsService)
  usersService = inject(UsersService)
  route = inject(ActivatedRoute)
  toastService = inject(ToastService)
  translateService = inject(TranslateService)
  callsService = inject(CallsService)
  dialog = inject(MatDialog)
  userSettings$ = this.settingsService.getUserSettings()
  currentUser$ = this.usersService.currentUser$
  organizationSettings$ = this.settingsService.getOrganizationSettings()
  userData!: User & { joinSettings: JoinSettings }
  organizationData!: Organization
  logger = inject(LoggerService)
  metricsService = inject(MetricsService)
  saveUserSettingsMutation = this.settingsService.updateUserSettings()
  saveOrganizationSettingsMutation = this.settingsService.updateOrganizationSettings()
  unlinkCalendarProviderMutation = this.settingsService.unlinkCalendarProviderMutation()
  section$ = this.route.params.pipe(
    map((params) => params['section']),
    distinctUntilChanged()
  )

  ngOnInit(): void {
    this.setUserData()
    this.setOrganizationData()
    this.initializeUserLocale()
  }

  analyticsDashboardOrganizationIds$: Observable<number[]> = this.currentUser$.pipe(
    filter((user) => {
      const organizationId = user?.organization?.id ?? null
      return organizationId !== null
    }),
    map((user) => [user!.organization.id])
  )

  getSectionTranslationKey(section: string) {
    const translationKey = SectionTranslationKeyMap[section]
    if (translationKey === undefined) {
      this.logger.error(`No translation key found for section: ${section}`)
      return 'COMMON.SETTINGS'
    }
    return translationKey
  }

  private initializeUserLocale() {
    this.userSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => {
        if (data?.locale) {
          const localeMap: { [key: string]: string } = {
            EN_US: 'en_US',
            EN_GB: 'en_GB',
          }
          const translationKey = localeMap[data.locale]
          if (translationKey) {
            this.translateService.use(translationKey)
          }
        }
      })
  }

  private setUserData() {
    this.userSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => (this.userData = data!))
  }

  private setOrganizationData() {
    this.organizationSettings$
      .pipe(
        filter((result) => result.isSuccess),
        map((result) => result.data)
      )
      .subscribe((data) => (this.organizationData = data!))
  }

  async updateUser() {
    if (this.userData.locale) {
      const localeMap: { [key: string]: string } = {
        EN_US: 'en_US',
        EN_GB: 'en_GB',
      }
      const translationKey = localeMap[this.userData.locale]
      if (translationKey) {
        this.translateService.use(translationKey)
      }
    }

    await this.saveUserSettingsMutation.mutateAsync(this.userData)
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  async updateOrganization() {
    await this.saveOrganizationSettingsMutation.mutateAsync(this.organizationData)
    this.toastService.show('SETTINGS.UPDATED_NOTIFICATION')
  }

  async updateMinutesStyling(minutesStyling: Partial<MinutesStyling>) {
    this.organizationData = { ...this.organizationData, ...minutesStyling }
    await this.metricsService.sendMetric(null, MetricType.CHANGE_MINUTING_STYLES, minutesStyling)
  }

  async unlinkCalendarProvider(provider: CalendarProviderName) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('SETTINGS.UNLINK_CONFIRMATION.DIALOG_TITLE'),
        description: this.translateService.instant('SETTINGS.UNLINK_CONFIRMATION.DIALOG_BODY'),
        confirmLabel: this.translateService.instant('COMMON.YES'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.unlinkCalendarProviderMutation.mutateAsync({ provider })
        this.toastService.show('SETTINGS.UNLINK_SUCCESSFUL')
      }
    })
  }

  linkCalendarProvider(provider: CalendarProviderName) {
    window.location.href = `/api/users/me/calendar-connect?provider=${provider}`
  }

  isPrimaryEmail(providerSpecificEmail: string) {
    return this.userData.email === providerSpecificEmail
  }

  isGoogleProvider(provider: CalendarProviderName): boolean {
    return provider === 'GOOGLE' || provider.includes('GOOGLE')
  }

  protected readonly MinutesPassive = MinutesPassive

  async toggleJoinSettingsFreezing() {
    const initialValue = this.organizationData.freezeJoinSettings
    this.organizationData.freezeJoinSettings = !this.organizationData.freezeJoinSettings
    if (initialValue) {
      this.toastService.show('SETTINGS.JOIN_SETTINGS_UNFROZEN')
      await this.updateOrganization()
    } else {
      this.confirmFreezeJoinSettings(initialValue)
    }
  }

  private confirmFreezeJoinSettings(initialValue: boolean) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('SETTINGS.CONFIRM_FREEZE_JOIN_SETTINGS_DIALOG.TITLE'),
        description: this.translateService.instant('SETTINGS.CONFIRM_FREEZE_JOIN_SETTINGS_DIALOG.DESCRIPTION'),
      },
    })
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        await this.actuallyFreezeJoinSettings()
      } else {
        this.organizationData.freezeJoinSettings = initialValue
      }
    })
  }

  private async actuallyFreezeJoinSettings() {
    this.organizationData.freezeJoinSettings = true
    await this.updateOrganization()
  }

  async updateSendUpcomingCallReminderEmail(value: boolean) {
    this.userData.sendUpcomingCallReminderEmail = value
    await this.updateUser()
  }

  async updateNotifyWhenPostProcessingCompletes(value: boolean) {
    this.userData.notifyWhenPostProcessingCompletes = value
    await this.updateUser()
  }

  async updateSendWeeklyDigestEmail(value: boolean | null) {
    this.userData.sendWeeklyDigestEmail = value
    await this.updateUser()
  }
}
